function buildShopifySrcset(src) {
  if (!src) return '';
  const widths = [360, 533, 720, 940, 1066, 1445, 1680, 1946];
  return widths
    .map((w) => {
      let url;
      if (src.includes('width=')) {
        url = src.replace(/width=\d+/, `width=${w}`);
      } else if (src.includes('?')) {
        url = `${src}&width=${w}`;
      } else {
        url = `${src}?width=${w}`;
      }
      return `${url} ${w}w`;
    })
    .join(', ');
}

class CollectionGridManager {
  constructor() {
    this.mqlMobile = window.matchMedia('(max-width: 749px)');
    this.mqlTablet = window.matchMedia('(min-width: 750px) and (max-width: 999px)');
    
    this.init();
    this.bindEvents();
    this.observeMutations();
  }

  init() {
    this.productGrid = document.getElementById('product-grid');
    if (!this.productGrid) return;

    this.defaultMobile = this.productGrid.getAttribute('data-cols-mobile') || '4';
    this.defaultTablet = this.productGrid.getAttribute('data-cols-tablet') || '4';
    this.defaultDesktop = this.productGrid.getAttribute('data-cols-desktop') || '4';

    // Ensure we start with the right layout and active button
    this.applyDeviceGrid();

    if (!this.isProductModelViewEnabled()) {
      this.updateView('product', true);
      return;
    }

    // Check currently active view button and apply view instantly
    this.applyActiveView(true);
  }

  applyActiveView(isInstant = false) {
    if (!this.isProductModelViewEnabled()) return;

    const applyView = () => {
      const viewToggleBtns = document.querySelectorAll('.js-view-toggle-btn');
      if (!viewToggleBtns.length) return;

      const activeBtn = Array.from(viewToggleBtns).find((btn) => btn.classList.contains('active'));
      if (activeBtn) {
        this.updateView(activeBtn.getAttribute('data-view'), isInstant);
      }
    };

    applyView();
    window.setTimeout(applyView, 100);
    window.setTimeout(applyView, 400);
  }

  isProductModelViewEnabled() {
    return this.productGrid?.dataset.productModelView === 'true';
  }

  bindEvents() {
    // Listen for resize
    const handleDeviceChange = this.applyDeviceGrid.bind(this);
    if (this.mqlMobile && this.mqlMobile.addEventListener) {
      this.mqlMobile.addEventListener('change', handleDeviceChange);
      this.mqlTablet.addEventListener('change', handleDeviceChange);
    } else if (this.mqlMobile && this.mqlMobile.addListener) {
      this.mqlMobile.addListener(handleDeviceChange);
      this.mqlTablet.addListener(handleDeviceChange);
    }

    // Use event delegation for click events (since elements get replaced via AJAX)
    document.addEventListener('click', (e) => {
      const gridBtn = e.target.closest('.grid-toggle-btn');
      if (gridBtn) {
        this.handleGridToggleClick(gridBtn);
      }

      const viewBtn = e.target.closest('.js-view-toggle-btn');
      if (viewBtn) {
        e.preventDefault();
        if (!this.isProductModelViewEnabled()) return;
        this.handleViewToggleClick(viewBtn);
      }
    });
  }

  observeMutations() {
    // Watch for DOM changes from facets.js (e.g. after sorting/filtering)
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    
    this.observer = new MutationObserver((mutations) => {
      let shouldReinit = false;
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          shouldReinit = true;
          break;
        }
      }
      if (shouldReinit) {
        // Re-run initialization to re-attach productGrid and update view
        this.init();
      }
    });

    this.observer.observe(container, { childList: true, subtree: false });
  }

  applyDeviceGrid() {
    if (!this.productGrid) return;
    let targetGrid = this.defaultDesktop;
    
    let prefMobile = sessionStorage.getItem('collection_grid_pref_mobile');
    let prefTablet = sessionStorage.getItem('collection_grid_pref_tablet');
    let prefDesktop = sessionStorage.getItem('collection_grid_pref_desktop');

    if (this.mqlMobile.matches) {
      targetGrid = prefMobile || this.defaultMobile;
    } else if (this.mqlTablet.matches) {
      targetGrid = prefTablet || this.defaultTablet;
    } else {
      targetGrid = prefDesktop || this.defaultDesktop;
    }
    
    this.productGrid.setAttribute('data-desktop-columns', targetGrid);

    document.querySelectorAll('.grid-toggle-btn').forEach(btn => {
      if (btn.getAttribute('data-grid') === targetGrid) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });

    if (typeof window.adjustBannerHeights === 'function') {
      window.adjustBannerHeights();
    }
  }

  handleGridToggleClick(btn) {
    const gridColumns = btn.getAttribute('data-grid');
    if (!gridColumns || !this.productGrid) return;

    if (this.mqlMobile.matches) {
      sessionStorage.setItem('collection_grid_pref_mobile', gridColumns);
    } else if (this.mqlTablet.matches) {
      sessionStorage.setItem('collection_grid_pref_tablet', gridColumns);
    } else {
      sessionStorage.setItem('collection_grid_pref_desktop', gridColumns);
    }

    const container = btn.closest('.collection-grid-toggles');
    if (container) {
      container.querySelectorAll('.grid-toggle-btn').forEach(b => {
        b.classList.remove('active');
      });
      btn.classList.add('active');
    }

    document.querySelectorAll('.grid-toggle-btn').forEach(b => {
      if (b.getAttribute('data-grid') === gridColumns) {
        b.classList.add('active');
      } else {
        b.classList.remove('active');
      }
    });

    if (!document.startViewTransition) {
      this.productGrid.setAttribute('data-desktop-columns', gridColumns);
      if (typeof window.adjustBannerHeights === 'function') window.adjustBannerHeights();
    } else {
      document.startViewTransition(() => {
        this.productGrid.setAttribute('data-desktop-columns', gridColumns);
        if (typeof window.adjustBannerHeights === 'function') window.adjustBannerHeights();
      });
    }
  }

  handleViewToggleClick(btn) {
    const view = btn.getAttribute('data-view');
    document.querySelectorAll('.js-view-toggle-btn').forEach(b => {
      if (b.getAttribute('data-view') === view) {
        b.classList.add('active');
      } else {
        b.classList.remove('active');
      }
    });

    this.updateView(view, false);
  }

  updateView(view, isInstant = false) {
    if (!this.productGrid) return;
    if (!this.isProductModelViewEnabled()) {
      view = 'product';
    }
    if (view === 'model') {
      this.productGrid.classList.add('model-view-active');
    } else {
      this.productGrid.classList.remove('model-view-active');
    }

    const listItems = this.productGrid.querySelectorAll('li.js-grid__item');
    listItems.forEach((li) => {
      this.updateCardImage(li, view, isInstant);
    });

    if (typeof window.adjustBannerHeights === 'function') {
      window.adjustBannerHeights();
    }
  }

  updateCardImage(li, view, isInstant = false) {
    if (li.dataset.duplicateProductPair === 'true') {
      const sliderComponent = li.querySelector('flickity-product-slider, card-product-slider');
      const image =
        li.querySelector('.js-card-view-image') ||
        li.querySelector('.js-card-product-primary-image') ||
        sliderComponent?.querySelector('.card__media img');

      if (!image) return;

      const productSrc = sliderComponent?.dataset.productImageSrc || image.dataset.productImageSrc;
      const modelSrc = sliderComponent?.dataset.modelImageSrc || image.dataset.modelImageSrc;

      const targetSrc = view === 'model' ? modelSrc : productSrc;
      if (targetSrc) {
        image.src = targetSrc;
        image.removeAttribute('srcset');
      }
      return;
    }

    const sliderComponent = li.querySelector('flickity-product-slider, card-product-slider');
    const image =
      li.querySelector('.js-card-view-image') ||
      li.querySelector('.js-card-product-primary-image') ||
      sliderComponent?.querySelector('.card__media img');

    if (!image) return;

    const productSrc = sliderComponent?.dataset.productImageSrc || image.dataset.productImageSrc;
    const modelSrc = sliderComponent?.dataset.modelImageSrc || image.dataset.modelImageSrc;

    const targetSrc = view === 'model' ? modelSrc : productSrc;
    if (targetSrc) {
      image.src = targetSrc;
      image.removeAttribute('srcset');
    }

    const nativeSlider = sliderComponent?.querySelector('.slider');
    if (nativeSlider) {
      nativeSlider.scrollTo({
        left: 0,
        behavior: isInstant ? 'auto' : 'smooth',
      });
    }

    if (sliderComponent?.flickity) {
      sliderComponent.flickity.select(0, false, true);
      sliderComponent.flickity.resize();
    } else if (sliderComponent?.tagName === 'FLICKITY-PRODUCT-SLIDER') {
      this.resetFlickitySlider(sliderComponent);
    }
  }

  resetFlickitySlider(sliderComponent, attempt = 0) {
    if (sliderComponent.flickity) {
      sliderComponent.flickity.select(0, false, true);
      sliderComponent.flickity.resize();
      return;
    }

    if (attempt < 10) {
      window.setTimeout(() => {
        this.resetFlickitySlider(sliderComponent, attempt + 1);
      }, 100);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new CollectionGridManager();
});
